import { Card, CardContent } from "@/components/ui/card";
import { ShoppingBag, Clock, MessageSquare, CheckCircle2, ShieldCheck, GaugeCircle, ShieldAlert } from "lucide-react";
import type { MarketingSettings } from "@/types/sms";

interface HowItWorksProps {
  settings: MarketingSettings;
  sendLabel: string;
  followUps: number;
}

const pct = (v: number) => `${Math.round(v * 100)}%`;

/**
 * "How it works" — the 4-step flow so the owner never has to guess, plus the
 * three safety-rails as a labeled strip (captain's "make this empty space
 * useful" note). Read-only, copy-only.
 */
export function HowItWorksCard({ settings, sendLabel, followUps }: HowItWorksProps) {
  const steps = [
    {
      icon: ShoppingBag,
      title: "A customer orders",
      body: `Everyone who ordered yesterday is in today’s ${sendLabel} batch; today’s orders go out tomorrow.`,
    },
    {
      icon: Clock,
      title: `Once a day at ${sendLabel}`,
      body: "One batch goes out daily. If the time falls inside quiet hours, it waits until sending is allowed.",
    },
    {
      icon: MessageSquare,
      title: "One invite each",
      body: "Only customers with a valid mobile who never replied to us get a single invite — never twice in a row.",
    },
    {
      icon: CheckCircle2,
      title: "They reply Y or STOP",
      body:
        followUps === 0
          ? "Reply Y and they join with a welcome offer; STOP removes them for good."
          : "Reply Y and they join with a welcome offer; STOP removes them for good. A quiet customer may get one more nudge after ordering again.",
    },
  ];

  const rails = [
    { icon: ShieldCheck, title: "Never over-texted", body: `At most ${1 + followUps} invite${followUps === 0 ? "" : "s"} ever per customer.` },
    {
      icon: GaugeCircle,
      title: "Auto-pauses itself",
      body: `Stops on its own if opt-outs pass ${pct(settings.invite_pause_optout_rate)} or failures pass ${pct(settings.invite_pause_failure_rate)}.`,
    },
    { icon: ShieldAlert, title: "Emergency stop", body: "One switch pauses every marketing text store-wide, instantly." },
  ];

  return (
    <Card>
      <CardContent className="space-y-5 p-4 sm:p-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">How it works</h3>
          <p className="mt-1 text-sm text-muted-foreground">No guessing: here is exactly what Order Invite does.</p>
        </div>

        <ol className="space-y-3">
          {steps.map((step, i) => (
            <li key={step.title} className="flex gap-3">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <step.icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">
                  <span className="mr-1 text-muted-foreground">{i + 1}.</span>
                  {step.title}
                </p>
                <p className="text-sm text-muted-foreground">{step.body}</p>
              </div>
            </li>
          ))}
        </ol>

        <div className="rounded-lg border border-border bg-muted/40 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Safety rails</p>
          <div className="grid gap-3 sm:grid-cols-3">
            {rails.map((rail) => (
              <div key={rail.title} className="flex gap-2 sm:flex-col sm:gap-1">
                <rail.icon className="h-4 w-4 shrink-0 text-status-success" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-xs font-medium text-foreground">{rail.title}</p>
                  <p className="text-xs text-muted-foreground">{rail.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
